// Library methods
import { useTranslation } from "react-i18next";

// Components
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { useTheme, Theme } from "@mui/material/styles";

// Hooks
import { useEMRWorklistDay } from "src/services/emr/hooks/emr-connections/useEMRWorklistDay";

// Types
import { EMRWorklistDayType } from "src/services/emr/types";

// Styles
import { getSurfaceTokens } from "src/styles/mui/muiStylesHelper";

type WorklistDayCounts = Pick<
  EMRWorklistDayType,
  "onWorklist" | "new" | "returning" | "waiting"
>;

export const toLocalIsoDate = (date: Date) =>
  [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, "0"),
    String(date.getDate()).padStart(2, "0"),
  ].join("-");

const getSegmentColors = (theme: Theme) => {
  const isDark = theme.palette.mode === "dark";
  const waitingStripe = isDark ? "#5B6D7A" : "#A3B1BC";
  const waitingGround = isDark ? "#2A2E44" : "#EEF1F4";

  return {
    track: isDark ? "#5B6D7A40" : "#E5E9ED",
    new: isDark ? "#FFFFFF" : "#202338",
    returning: isDark ? "#8C9BA8" : "#A3B1BC",
    waiting: `repeating-linear-gradient(45deg, ${waitingStripe} 0 2px, ${waitingGround} 2px 5px)`,
  };
};

const percentOf = (part: number, whole: number) =>
  whole > 0 ? `${(part / whole) * 100}%` : "0%";

export const WorklistTodayPanel = ({
  counts,
  dayLabel,
  lastReceivedLabel,
}: {
  counts: WorklistDayCounts;
  dayLabel: string;
  lastReceivedLabel?: string;
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { mutedText, onSurface } = getSurfaceTokens(theme);
  const colors = getSegmentColors(theme);

  const segments = [
    { key: "new", label: t("new"), value: counts.new, color: colors.new },
    {
      key: "returning",
      label: t("returning"),
      value: counts.returning,
      color: colors.returning,
    },
    {
      key: "waiting",
      label: t("waiting"),
      value: counts.waiting,
      color: colors.waiting,
    },
  ].filter((segment) => segment.value > 0);

  return (
    <Box
      data-testid="worklist-today"
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "8px",
        p: "8px",
        borderRadius: "4px",
        backgroundColor:
          theme.palette.mode === "dark" ? "#5B6D7A40" : "#5B6D7A20",
        flexBasis: "100%",
      }}
    >
      <Box display="flex" justifyContent="space-between" gap="8px">
        <Typography fontSize="12px" color={mutedText}>
          {dayLabel}
        </Typography>
        {lastReceivedLabel && (
          <Typography fontSize="12px" color={mutedText}>
            {lastReceivedLabel}
          </Typography>
        )}
      </Box>

      {counts.onWorklist === 0 ? (
        <Typography fontSize="14px" fontWeight={600} color={onSurface}>
          {t("No patients on today's worklist")}
        </Typography>
      ) : (
        <>
          <Box display="flex" alignItems="baseline" gap="8px">
            <Typography
              data-testid="worklist-today-new"
              fontSize="28px"
              fontWeight={700}
              lineHeight={1}
              color={onSurface}
            >
              {counts.new}
            </Typography>
            <Typography fontSize="14px" fontWeight={600} color={onSurface}>
              {t("new patients imported")}
            </Typography>
          </Box>
          <Box
            role="img"
            aria-label={segments
              .map((segment) => `${segment.value} ${segment.label}`)
              .join(", ")}
            sx={{
              display: "flex",
              height: "8px",
              borderRadius: "4px",
              overflow: "hidden",
              backgroundColor: colors.track,
            }}
          >
            {segments.map((segment) => (
              <Box
                key={segment.key}
                sx={{
                  width: percentOf(segment.value, counts.onWorklist),
                  background: segment.color,
                }}
              />
            ))}
          </Box>
          <Box
            display="flex"
            flexWrap="wrap"
            columnGap="14px"
            rowGap="4px"
            fontSize="13px"
            color={onSurface}
          >
            {segments.map((segment) => (
              <Box
                key={segment.key}
                data-testid={`worklist-today-${segment.key}-legend`}
                component="span"
                display="flex"
                alignItems="center"
                gap="6px"
              >
                <Box
                  component="span"
                  sx={{
                    width: "10px",
                    height: "10px",
                    borderRadius: "2px",
                    background: segment.color,
                  }}
                />
                {segment.value} {segment.label}
              </Box>
            ))}
          </Box>
        </>
      )}
    </Box>
  );
};

type WorklistTodayType = {
  clinicId: string | undefined;
  connectionId: string;
};

const WorklistToday = ({ clinicId, connectionId }: WorklistTodayType) => {
  const { t } = useTranslation();
  const today = new Date();
  const { data } = useEMRWorklistDay(
    clinicId,
    connectionId,
    toLocalIsoDate(today)
  );

  if (!data) return null;

  const dayLabel = `${t("Today")}, ${today.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  })}`;
  const lastReceivedLabel = data.lastReceivedAt
    ? t("Last received {{time}}", {
        time: new Date(data.lastReceivedAt).toLocaleString(undefined, {
          dateStyle: "short",
          timeStyle: "short",
        }),
      })
    : undefined;

  return (
    <WorklistTodayPanel
      counts={data}
      dayLabel={dayLabel}
      lastReceivedLabel={lastReceivedLabel}
    />
  );
};

export default WorklistToday;
